import {
  type Path,
  FormProvider,
  useForm,
  type SubmitErrorHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dynamic from 'next/dynamic';

import { type OnSubmitHandler } from '../../../../types/forms.typed';
import {
  type BankOfIrelandForm,
  bankofirelandFormSchema,
  type QueryParameters,
  InitialFormValues,
  bankOfIrelandCollapsiblePanelSchema,
  filterValidPanelNames,
} from '../../../../components/CarFinancePage/queries/schema/carFinance.schema';
import { SubmitPanel } from '../shared/SubmitPanel/SubmitPanel';
import { ResidentialDetails } from './panels/ResidentialDetails/ResidentialDetails';
import { EmploymentDetails } from './panels/EmploymentDetails/EmploymentDetails';
import { FinanceCalculator } from '../../../../components/CarFinancePage/components/FinanceCalculator/FinanceCalculator';
import { PersonalDetails } from '../../../../components/CarFinancePage/components/Panels/PersonalDetails/PersonalDetails';
import { BankingInformation } from './panels/BankingInformation/BankingInformation';
import { DataProtectionAndConsents } from './panels/DataProtectionAndConsents/DataProtectionAndConsents';

import { useSubmitFinanceForm } from '../../../../components/CarFinancePage/queries/hooks/useSubmitFinanceForm';
import { usePanelDispatch } from '../shared/PanelContext';
import { TotalDepositProvider } from '../../contexts/TotalDepositContext';

import { formatDate } from '../formatting';
import { Spacer } from '../../../../helpers/genericStyles';

import {
  requiredDefaultValuesLoanDetails,
  clearableRequiredFieldsDefaultValuesBankingDetails,
  clearableRequiredFieldsDefaultValuesCurrentAddress,
  clearableRequiredFieldsDefaultValuesEmploymentDetails,
  clearableRequiredFieldsDefaultValuesPersonalDetails,
  clearableRequiredFieldsDefaultValuesPreviousAddress,
} from '../shared/constants/clearableRequiredDefaultValues';

import type { UsePromptUserOnChangeBeforeLeavingModalParams } from '../../../../components/CarFinancePage/components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal.typed';
import { formatValidationErrors } from '../utils/formatValidationErrors/formatValidationErrors';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';
import { FEATURE_CAR_FINANCE_UPDATES } from '../../../../utils';
import { FinanceCalculatorV2 } from '../../components/FinanceCalculatorV2/FinanceCalculatorV2';

const PromptUserOnChangeBeforeLeavingModal =
  dynamic<UsePromptUserOnChangeBeforeLeavingModalParams>(
    import(
      '../../../../components/CarFinancePage/components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal'
    ).then((mod) => mod.PromptUserOnChangeBeforeLeavingModal),
  );

type BankOfIrelandFinanceFormProps = {
  query: QueryParameters;
  onSubmitCallback: () => void;
  onSubmitErrorCallback: (errorFields: Array<string>) => void;
  initialFormData: InitialFormValues;
  adId: string;
};

const BankOfIrelandFinanceForm = (props: BankOfIrelandFinanceFormProps) => {
  const {
    query: {
      dealerId,
      provider,
      minimumDepositPercentage,
      minimumDepositValue,
    },
    onSubmitCallback,
    onSubmitErrorCallback,
    initialFormData,
    adId,
  } = props;
  const methods = useForm<BankOfIrelandForm>({
    resolver: zodResolver(bankofirelandFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      /**
       * We initialise the form with these values to determine what constitutes an invalid value for a field.
       * A panel with one or more invalid field values will be considered "incomplete".
       *
       * Once input is provided, it is compared to these values. If all the required values for a particular panel
       * are valid, that panel is then considered "complete".
       */
      personalDetails: clearableRequiredFieldsDefaultValuesPersonalDetails,
      currentAddress: {
        ...clearableRequiredFieldsDefaultValuesCurrentAddress,
        addressLine3: '',
      },
      previousAddress: clearableRequiredFieldsDefaultValuesPreviousAddress,
      employmentDetails: clearableRequiredFieldsDefaultValuesEmploymentDetails,
      previousEmploymentDetails: {
        status: '',
        previousOccupation: '',
        previousEmployerName: '',
        timeWithPreviousEmployer: {
          years: '',
        },
      },
      bankingDetails: clearableRequiredFieldsDefaultValuesBankingDetails,
      accountDetails: {
        sortCode: '',
        accountNumber: '',
      },
      loanDetails: {
        ...initialFormData.loanDetails,
        ...requiredDefaultValuesLoanDetails,
      },
      consent: {
        dataConsent: false,
        acceptDocuments: false,
      },
      carDetails: initialFormData.carDetails,
      hiddenFields: initialFormData.hiddenFields,
    },
  });

  const dispatch = usePanelDispatch();

  const validateBankOfIrelandErrors = (errors: Array<string>) => {
    return filterValidPanelNames(bankOfIrelandCollapsiblePanelSchema, errors);
  };

  const { mutate, isPending } = useSubmitFinanceForm({
    onValidationError: (validationErrors) => {
      for (const error of validationErrors) {
        // Non-trivial to correctly infer the type of the error.field
        // No error thrown when setting error on a field that does not exist
        // Not ideal but does not break the form
        methods.setError(error.field as Path<BankOfIrelandForm>, {
          message: error.message,
        });
      }
      const fieldsWithErrors = validationErrors.map((error) => error.field);
      const panelsWithErrors = validateBankOfIrelandErrors(
        formatValidationErrors(fieldsWithErrors),
      );
      dispatch({
        type: 'OPEN_PANELS',
        panels: panelsWithErrors,
      });
      // Scroll the top error into view
      methods.setFocus(validationErrors[0].field as Path<BankOfIrelandForm>);
      onSubmitErrorCallback(fieldsWithErrors);
    },
    onSuccess: onSubmitCallback,
  });

  const onSubmit: OnSubmitHandler<typeof methods> = async (values) => {
    const formattedValues = {
      ...values,
      personalDetails: {
        ...values.personalDetails,
        dateOfBirth: formatDate(
          values.personalDetails.dateOfBirth.toLocaleDateString('en-IE'),
        ),
      },
    };

    mutate(formattedValues);
  };

  const onInvalidBankOfIrelandSubmission: SubmitErrorHandler<
    typeof methods
  > = async (errors) => {
    const fieldsWithErrors = typeSafeObjectKeys(errors);
    const panelsWithErrors = validateBankOfIrelandErrors(fieldsWithErrors);
    dispatch({
      type: 'OPEN_PANELS',
      panels: panelsWithErrors,
    });
    onSubmitErrorCallback(fieldsWithErrors);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(
            onSubmit,
            onInvalidBankOfIrelandSubmission,
          )}
          noValidate
        >
          <TotalDepositProvider minimumDepositValue={minimumDepositValue}>
            {FEATURE_CAR_FINANCE_UPDATES ? (
              <FinanceCalculatorV2
                dealerId={dealerId}
                financeHouse={provider}
                minimumDepositPercentage={minimumDepositPercentage}
                minimumDepositValue={minimumDepositValue}
                adId={adId}
              />
            ) : (
              <FinanceCalculator
                dealerId={dealerId}
                financeHouse={provider}
                minimumDepositPercentage={minimumDepositPercentage}
                minimumDepositValue={minimumDepositValue}
              />
            )}
            <Spacer marginBottom={['S8', 'M16']} />
            <PersonalDetails />
            <ResidentialDetails />
            <EmploymentDetails />
            <BankingInformation />
            <DataProtectionAndConsents />
            <SubmitPanel isSubmitting={isPending} />
            <Spacer marginBottom="L40" />
          </TotalDepositProvider>
        </form>
      </FormProvider>
      <PromptUserOnChangeBeforeLeavingModal
        promptUser={methods.formState.isDirty}
      />
    </>
  );
};

export { BankOfIrelandFinanceForm };
